import {
  faPeopleGroup,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Axios from "axios";

import React, { useState, useEffect, useRef } from "react";

import "./loader_spinner.css";

/**
 *
 * @param {teams} teams - The teams of the company
 * @param {selectedTeam} selectedTeam - The selected team
 * @param {setSelectedTeam} setSelectedTeam - The function to set the selected team
 * @param {setTeamEmployees} setTeamEmployees - The function to set the employees of the selected team
 * @param {reloadTeams} reloadTeams - The boolean to reload the teams
 * @param {setReloadTeams} setReloadTeams - The function to set the boolean to reload the teams
 * @param {teamsLoading} teamsLoading - The boolean to check if the teams are loading
 * @param {role} role - The role of the current user (admin, company_admin, manager, logistics_specialist)
 * @returns {JSX.Element} - The team selection dropdown component
 */
const TeamSelection = ({
  selectedCompany,
  teams,
  selectedTeam,
  setSelectedTeam,
  setTeamEmployees,
  reloadTeams,
  setReloadTeams,
  teamsLoading,
  role,
  user,
  onTeamSelect, // New prop to handle team selection
  onTeamSelectComplete, // New prop to handle completion callback
}) => {
  const [viewOptions, setViewOptions] = useState(false);
  const [createNewTeamView, setCreateNewTeamView] = useState(false);
  const [editTeamView, setEditTeamView] = useState(false);
  const [fetchingTeamDetails, setFetchingTeamDetails] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const dropdownRef = useRef(null);
  const boxRef = useRef(null);

  const toggleView = () => {
    // Don't toggle if teams are loading or fetching team details
    if (teamsLoading || fetchingTeamDetails) return;
    setViewOptions(!viewOptions);
  };

  const handleSelect = (team) => {
    setViewOptions(false);
    setSelectedTeam(team);
    
    // If we have the onTeamSelect callback, use it to fetch full details
    if (onTeamSelect) {
      setFetchingTeamDetails(true);
      onTeamSelect(team.team_id, () => {
        setFetchingTeamDetails(false);
        if (onTeamSelectComplete) onTeamSelectComplete();
      });
    } else {
      // Fallback for backward compatibility
      setTeamEmployees(team.employees || []);
    }
  };



  const handleCloseDialogue = () => {
    // e.stopPropagation();
    setCreateNewTeamView(false);
    setEditTeamView(false);
    setViewOptions(false);
  };

  //You can open the edit team dialog here.

  const handleClickOutside = (event) => {
    if (
      boxRef.current &&
      dropdownRef.current &&
      !boxRef.current.contains(event.target) &&
      !dropdownRef.current.contains(event.target)
    ) {
      setViewOptions(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);



  //  useeffect to filter the employees and only show the current user data from the team when the user is an employee
  useEffect(() => {
    if (role === "employee" && selectedTeam?.employees) {
      const filteredEmployees = selectedTeam.employees.filter(
        (employee) => employee.id === user.id
      );
      setTeamEmployees(filteredEmployees);
    }
  }, [selectedTeam, role, user.id]);

  // Filter teams based on search query
  const filteredTeams = teams.filter((team) =>
    team.team_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      {role === "employee" ? (
        <>
          <h1 className="flex text-gray-600 justify-between items-center bg-gray-200 rounded-lg w-full text-sm px-2 py-1 relative">
            Team Name : {selectedTeam?.team_name}
          </h1>
        </>
      ) : (
        <div
          className={`teamSelect flex justify-between items-center bg-gray-200 rounded-lg w-full text-sm px-2 py-1 relative ${(teamsLoading || fetchingTeamDetails) ? 'cursor-wait opacity-70' : 'cursor-pointer'}`}
          onClick={toggleView}
          style={{ userSelect: "none" }}
          ref={boxRef}
        >
          <div className="text-gray-600 font-small flex items-center">
            {teamsLoading ? (
              <>
                 <div className="loader ease-linear rounded-full border-2 border-t-2 border-gray-400 h-4 w-4 mr-2"></div>
                 <span className="text-gray-400">Loading teams...</span>
              </>
            ) : fetchingTeamDetails ? (
              <>
                 <div className="loader ease-linear rounded-full border-2 border-t-2 border-gray-400 h-4 w-4 mr-2"></div>
                 <span className="text-gray-400">Loading team details...</span>
              </>
            ) : selectedTeam ? (
              selectedTeam?.team_name
            ) : (
              <>
                <p className="flex justify-between text-gray-400 ml-1">
                  {teams?.length > 0
                    ? `Select a team from ${teams?.length}`
                    : "No teams available"}
                </p>
              </>
            )}
          </div>
          {!(teamsLoading || fetchingTeamDetails) && (
            selectedTeam ? (
              <FontAwesomeIcon icon={faPeopleGroup} className="text-gray-600" />
            ) : (
              <FontAwesomeIcon
                icon={faSearch}
                className="text-gray-600 cursor-pointer ml-2"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleView();
                }}
              />
            )
          )}
          {viewOptions && !teamsLoading && !fetchingTeamDetails && (
            <div
              ref={dropdownRef}
              className="absolute top-full left-0 bg-white rounded-lg shadow-md border dark-gray-scrollbar border-gray-300 w-full overflow-hidden z-40"
              style={{ top: "calc(100%)", maxHeight: "200px", overflowY: "auto" }}
            >
              <div className="w-full flex p-2">
                <input
                  type="text"
                  className="border p-1 w-full rounded-lg"
                  placeholder="Search teams..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
              </div>

              {filteredTeams
                .sort((a, b) => a.team_name.localeCompare(b.team_name))
                .map((team, index) => (
                  <div
                    key={index}
                    className="px-4 py-1 text-gray-600 font-normal cursor-pointer hover:bg-gray-100"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent toggleView from firing again
                      handleSelect(team);
                    }}
                  >
                    <h2 className="flex justify-between">
                      {team.team_name}{" "}
                      <span className="font-semibold">({team?.employee_count || team?.employees?.length || 0})</span>
                    </h2>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default TeamSelection;
